import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import { ContentIcon } from '@/components/atoms';
import { servicePath } from '@/content/services';

import { type ComparisonRow } from '../hooks/useServiceComparison';

import { compareGridSx } from './serviceCompareTokens';
import { ServiceCostChip } from './ServiceCostChip';

/**
 * One service as a row of the comparison band: name, outcome, cost, stage.
 *
 * The whole row is the anchor, for the reason `ServiceCard` gives: a link
 * inside a wide clickable-looking row gives a pointer a small target and a
 * screen reader seven links all named "Learn more". One anchor per row means
 * the accessible name is the service.
 *
 * It is a grid and not a `<table>`, and that is a decision rather than a
 * shortcut. A table of seven rows is the right element only while the columns
 * survive, and these do not: at phone width the four cells stack, at which
 * point a `<td>` whose `<th>` is a screen away is worse for a screen reader
 * than a self-describing block — "Free" and "Find the right car" say what
 * they are without a column heading, which is exactly the test for whether
 * the heading was carrying meaning or alignment. It was carrying alignment,
 * so it is `aria-hidden` in the band above and this is a list of links.
 */
export function ServiceCompareRow({ row }: { row: ComparisonRow }) {
  const { service, stage } = row;

  return (
    <Box
      component="li"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 1, borderColor: 'divider' },
      }}
    >
      <Box
        component={RouterLink}
        to={servicePath(service)}
        prefetch="intent"
        sx={{
          ...compareGridSx,
          py: { xs: 5, md: 4 },
          px: { xs: 0, md: 3 },
          mx: { xs: 0, md: -3 },
          borderRadius: 2,
          textDecoration: 'none',
          color: 'inherit',
          transition: 'background-color 200ms ease',
          '&:hover, &:focus-visible': {
            bgcolor: 'surfaceVariant',
          },
          '&:hover .ServiceCompare-name, &:focus-visible .ServiceCompare-name': {
            color: 'primary.main',
          },
          '&:hover .ServiceCompare-arrow, &:focus-visible .ServiceCompare-arrow': {
            transform: 'translateX(4px)',
            color: 'primary.main',
          },
        }}
      >
        {/* Service */}
        <Stack direction="row" spacing={3} sx={{ alignItems: 'center', minWidth: 0 }}>
          <ContentIcon
            name={service.icon}
            sx={{ flexShrink: 0, fontSize: 22, color: 'primary.main' }}
          />
          <Typography
            className="ServiceCompare-name"
            variant="subtitle2"
            component="h3"
            sx={{ fontWeight: 700, transition: 'color 200ms ease' }}
          >
            {service.title}
          </Typography>
        </Stack>

        {/* What you end up with */}
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
          {service.outcome}
        </Typography>

        {/* Cost */}
        <ServiceCostChip cost={service.cost} />

        {/* Stage, and the arrow that says the row goes somewhere */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}
        >
          <Typography
            variant="caption"
            // Tertiary, but still text: `text.disabled` is 2.9:1 at this size
            // and fails AA. `text.secondary` is 7.9:1 and still reads as the
            // quietest thing in the row.
            sx={{ fontWeight: 700, letterSpacing: '0.03em', color: 'text.secondary' }}
          >
            {stage}
          </Typography>
          <ArrowForwardRounded
            aria-hidden
            className="ServiceCompare-arrow"
            sx={{
              flexShrink: 0,
              fontSize: 18,
              color: 'text.disabled',
              transition: 'transform 250ms ease, color 250ms ease',
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
}
